import { AI_ASSISTANT_PACKAGE, formatDueDate, type AssistantPackage, type MyBusiness } from "@teamna/shared";
import { useEffect, useRef, useState } from "react";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useAssistantPackage } from "@teamna/web-client/hooks/use-assistant.ts";
import { Button, CheckIcon, ConfirmDialog, FormRow, FormSection, InlineError, SparkIcon, showToast } from "../../components/index.ts";

/**
 * AI Assistant is a separate package (decided 2026-10-21, ai-assistant-v1.md "The package"): AED 149
 * a month, plus VAT, as a second line on the Teamna plan. Two places use this file:
 *
 * - `AssistantOffer`: the AI Assistant page while the package is not on. What it does, the price,
 *   and "Add AI Assistant" for the owner or an admin.
 * - `AssistantPackageSection`: the AI Assistant part of the Billing page: on since, ends on, remove,
 *   keep.
 *
 * Teamna can also give it free in Control Center; then the page says so and has no Remove.
 */

const date = (iso: string | null, timeZone: string) => (iso ? formatDueDate(new Date(iso), timeZone) : "");

/** "AED 149 a month, plus VAT". */
export const ASSISTANT_PRICE_LINE = `AED ${AI_ASSISTANT_PACKAGE.priceAed} a month, plus VAT`;

/** The status of the package in one line, for the Billing page. Unit tested. */
export function assistantPackageLine(pkg: AssistantPackage, timeZone: string): string {
  if (pkg.purchased && pkg.endsAt) return `On until ${date(pkg.endsAt, timeZone)}. After that it stops, and you pay nothing more for it.`;
  if (pkg.purchased) return `On. ${ASSISTANT_PRICE_LINE}, with your plan.`;
  if (pkg.grant) return pkg.grant.until ? `On, free from Teamna until ${formatDueDate(new Date(`${pkg.grant.until}T12:00:00Z`), timeZone)}.` : "On, free from Teamna.";
  return `Not in your plan. ${ASSISTANT_PRICE_LINE}.`;
}

/** What the person agrees to with "Add AI Assistant". Unit tested. */
export function assistantAddTerms(pkg: AssistantPackage, timeZone: string, inTrial: boolean): string[] {
  const next = date(pkg.nextChargeAt, timeZone);
  if (pkg.endsAt) return [`AI Assistant stays on. The next charge for it is on ${next}, with your plan.`];
  const lines = inTrial
    ? [`AI Assistant starts now. It is free until your trial ends on ${next}.`, `Then it is ${ASSISTANT_PRICE_LINE}, with your plan.`]
    : [
        "AI Assistant starts now.",
        `Your next invoice, on ${next}, has ${ASSISTANT_PRICE_LINE} for the next month, and a part for the days left of this month.`,
      ];
  if (!pkg.hasCard) lines.push(`Add a card on the Billing page before ${next}.`);
  lines.push("You can remove it on the Billing page at any time. It then stays on until your next charge date, and you pay nothing more for it.");
  return lines;
}

/** "Add AI Assistant" with the confirm. `inTrial` changes the words only. */
function AddButton({ business, pkg, inTrial }: { business: MyBusiness; pkg: AssistantPackage; inTrial: boolean }) {
  const [open, setOpen] = useState(false);
  const change = useAssistantPackage(business.businessId);
  const keep = pkg.endsAt !== null;
  return (
    <>
      <Button onClick={() => setOpen(true)}>{keep ? "Keep AI Assistant" : "Add AI Assistant"}</Button>
      {change.error ? <InlineError error={change.error} /> : null}
      <ConfirmDialog
        open={open}
        title={keep ? "Keep AI Assistant?" : `Add AI Assistant for AED ${AI_ASSISTANT_PACKAGE.priceAed} a month?`}
        confirmLabel={keep ? "Keep it" : "Add AI Assistant"}
        cancelLabel="Not now"
        loading={change.isPending}
        onCancel={() => setOpen(false)}
        onConfirm={() =>
          change.mutate("add", {
            onSuccess: () => {
              setOpen(false);
              showToast(keep ? "AI Assistant stays on." : "AI Assistant is on. Open it from the menu.");
            },
            onError: () => setOpen(false),
          })
        }
      >
        <div className="flex flex-col gap-2 text-[14px] text-ink-2">
          {assistantAddTerms(pkg, business.timezone, inTrial).map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </ConfirmDialog>
    </>
  );
}

/** The business is in its free trial now (the billing summary of the business list). */
function inTrialOf(business: MyBusiness): boolean {
  return business.billing?.state === "trialing";
}

/** The AI Assistant page while the package is not on. */
export function AssistantOffer({ business }: { business: MyBusiness }) {
  const pkg = business.assistantPackage ?? null;
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !pkg) return;
    sent.current = true;
    track("assistant_offer_viewed", { screen: "assistant_page", can_add: pkg.canAdd });
  }, [pkg]);

  return (
    <section className="mx-auto flex w-full max-w-[560px] flex-col gap-5 rounded-card border border-line bg-paper p-6 shadow-card">
      <div className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent-surface text-accent">
          <SparkIcon size={24} />
        </span>
        <div>
          <h2 className="text-[19px]">Add AI Assistant to your plan</h2>
          <p className="text-[14px] text-ink-2">{ASSISTANT_PRICE_LINE}. You can remove it at any time.</p>
        </div>
      </div>
      <ul className="flex flex-col gap-2.5">
        {AI_ASSISTANT_PACKAGE.includes.map((line) => (
          <li key={line} className="flex gap-2.5 text-[14px]">
            <CheckIcon size={18} className="mt-0.5 shrink-0 text-accent" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <p className="text-[13px] text-ink-3">Only the owner and admins use it. It changes nothing until you tap Confirm.</p>
      {pkg?.canAdd ? (
        <div className="flex">
          <AddButton business={business} pkg={pkg} inTrial={inTrialOf(business)} />
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">
          {business.billing?.access === "read_only"
            ? "Desk is read-only now. Pay your plan on the Billing page first, then add AI Assistant here."
            : "Write to Teamna in Help and support to add AI Assistant to your plan."}
        </p>
      )}
    </section>
  );
}

/** The AI Assistant part of the Billing page. Nothing when the business list has no package (staff). */
export function AssistantPackageSection({ business }: { business: MyBusiness }) {
  const pkg = business.assistantPackage ?? null;
  const [removing, setRemoving] = useState(false);
  const change = useAssistantPackage(business.businessId);
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !pkg || pkg.on) return;
    sent.current = true;
    track("assistant_offer_viewed", { screen: "billing", can_add: pkg.canAdd });
  }, [pkg]);
  if (!pkg) return null;

  const live = pkg.purchased && !pkg.endsAt;
  return (
    <FormSection title="AI Assistant" description={assistantPackageLine(pkg, business.timezone)}>
      <FormRow label="Package" hint={pkg.on ? undefined : AI_ASSISTANT_PACKAGE.includes.join(". ")}>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          {live ? (
            <Button variant="ghost" onClick={() => setRemoving(true)}>
              Remove AI Assistant
            </Button>
          ) : pkg.canAdd && !pkg.grant ? (
            <AddButton business={business} pkg={pkg} inTrial={inTrialOf(business)} />
          ) : null}
          {change.error ? <InlineError error={change.error} /> : null}
        </div>
      </FormRow>
      <ConfirmDialog
        open={removing}
        title="Remove AI Assistant?"
        tone="danger"
        confirmLabel="Remove"
        loading={change.isPending}
        onCancel={() => setRemoving(false)}
        onConfirm={() =>
          change.mutate("remove", {
            onSuccess: (next) => {
              setRemoving(false);
              showToast(next.endsAt ? `AI Assistant stays on until ${date(next.endsAt, business.timezone)}.` : "AI Assistant is removed.");
            },
            onError: () => setRemoving(false),
          })
        }
      >
        <p className="text-[14px] text-ink-2">
          AI Assistant stays on until {date(pkg.nextChargeAt, business.timezone)}, the end of the time you paid for. After that it stops, and you pay
          nothing more for it. Your chats stay.
        </p>
      </ConfirmDialog>
    </FormSection>
  );
}
